import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { sendGetClientRequest, sendPostClientRequest } from "../../../api/helper";
import { sendApiClientRequestExcel } from "../../../api/api";
import { serialize } from "../../../utils";

const initialState = {
  rows: [],
  total: 0,
  page: 1,
  limit: 20,
  loading: false,
  error: null,
  filter: {
    search: "",
    status: "",
    type: "",
    buyer_type: "",
    date_from: "",
    date_to: "",
  },
  detail: null,
  detailLoading: false,
};

const buildQuery = ({ filter = {}, page, limit }) => {
  const q = {};
  if (filter.search) q.search = filter.search;
  if (filter.status) q.status = filter.status;
  if (filter.type) q.type = filter.type;
  if (filter.buyer_type) q.buyer_type = filter.buyer_type;
  if (filter.date_from) q.date_from = filter.date_from;
  if (filter.date_to) q.date_to = filter.date_to;
  if (page) q.page = page;
  if (limit) q.limit = limit;
  return Object.keys(q).length ? `?${serialize(q)}` : "";
};

const unwrapOrThrow = (response) => {
  if (!response || response.error) {
    const err = new Error(response?.msg || "API error");
    err.payload = response?.data;
    throw err;
  }
  return response.data;
};

export const fetchInvoices = createAsyncThunk(
  "invoices/fetch",
  async ({ filter, page, limit } = {}) => {
    const qs = buildQuery({ filter, page, limit });
    const response = await sendGetClientRequest(`/api/center/invoices${qs}`);
    return unwrapOrThrow(response) ?? { rows: [], total: 0 };
  }
);

export const fetchInvoiceDetail = createAsyncThunk(
  "invoices/fetchDetail",
  async (invoiceId) => {
    const response = await sendGetClientRequest(
      `/api/center/invoices/${invoiceId}`
    );
    return unwrapOrThrow(response);
  }
);

export const queryInvoice = createAsyncThunk(
  "invoices/query",
  async ({ invoiceId }) => {
    const response = await sendPostClientRequest(
      `/api/center/invoices/${invoiceId}/query`
    );
    return unwrapOrThrow(response);
  }
);

export const syncAllInvoices = createAsyncThunk(
  "invoices/syncAll",
  async ({ filter } = {}) => {
    const qs = buildQuery({ filter });
    const response = await sendPostClientRequest(
      `/api/center/invoices/sync-all${qs}`
    );
    return unwrapOrThrow(response);
  }
);

export const voidInvoice = createAsyncThunk(
  "invoices/void",
  async ({ invoiceId, invoiceNum, invoiceRandNum, reason }) => {
    const response = await sendPostClientRequest(
      `/api/center/invoices/${invoiceId}/void`,
      {
        invoice_num: invoiceNum,
        invoice_rand_num: invoiceRandNum,
        reason: reason || "中控作廢",
      }
    );
    return unwrapOrThrow(response);
  }
);

export const exportInvoices = createAsyncThunk(
  "invoices/export",
  async ({ filter } = {}) => {
    const qs = buildQuery({ filter });
    const stamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);
    await sendApiClientRequestExcel(
      "get",
      `/api/center/invoices/export${qs}`,
      null,
      false,
      "blob",
      `invoices_${stamp}`
    );
  }
);

const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    setFilter: (state, { payload }) => {
      state.filter = { ...state.filter, ...payload };
      state.page = 1;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    resetDetail: (state) => {
      state.detail = null;
    },
  },
  extraReducers: {
    [fetchInvoices.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [fetchInvoices.fulfilled]: (state, { payload }) => {
      state.loading = false;
      state.rows = Array.isArray(payload?.rows) ? payload.rows : [];
      state.total = Number.isFinite(payload?.total) ? payload.total : 0;
      if (Number.isInteger(payload?.page) && payload.page > 0) {
        state.page = payload.page;
      }
      if (Number.isInteger(payload?.limit) && payload.limit > 0) {
        state.limit = payload.limit;
      }
    },
    [fetchInvoices.rejected]: (state, { error }) => {
      state.loading = false;
      state.error = error?.message ?? "fetch failed";
    },
    [fetchInvoiceDetail.pending]: (state) => {
      state.detailLoading = true;
    },
    [fetchInvoiceDetail.fulfilled]: (state, { payload }) => {
      state.detailLoading = false;
      state.detail = payload;
    },
    [fetchInvoiceDetail.rejected]: (state) => {
      state.detailLoading = false;
    },
  },
});

export const { setFilter, setPage, resetDetail } = invoicesSlice.actions;
export const selectInvoices = (state) => state.invoices;
export default invoicesSlice.reducer;
